import z from 'zod';
import { DeepPartial, Draft } from '../../common';
import { IFieldErrors, ValidationError } from '../../validation';

import { IData, getSchema as getDataSchema } from '../common/dataSchema';
import { IRequestMeta, MetaSchema } from '../request';
import { diff } from 'deep-object-diff';
import { cleanObject } from 'src/utils';

export interface IDIRequest<T> {
  userId: string;
  categoryId?: string;
  recordCategoryType: string;
  data?: T;
  original?: Partial<T>;
  changes?: Partial<T>;
  meta: IRequestMeta;
}

const BaseDiRequest = z.object({
  userId: z.string().min(1),
});

const getSchema = (update: Draft<DeepPartial<IDIRequest<IData>>>, type: string, initial?: Partial<IData>) => {
  return BaseDiRequest.extend({
    categoryId: type === 'create' ? z.string().min(1) : z.string().optional(),
    data: getDataSchema(update.data || {}, type, initial || {}),
    meta: MetaSchema,
  });
};

export const validate = (update: DeepPartial<IDIRequest<IData>>, type: string, original?: Partial<IData>) => {
  let changes = undefined;
  const schema = getSchema(update, type, original);
  try {
    const validated = schema.parse({ ...update, meta: update.meta });
    if (original) {
      // Ensure there are changes from the original record
      changes = diff(cleanObject(original), cleanObject(validated.data));
      const hasChanges = Object.keys(changes).length > 0;
      if (!hasChanges) {
        throw new z.ZodError([
          {
            code: 'invalid_type',
            expected: 'object',
            received: 'undefined',
            path: ['diff'],
            message: 'No changes were detected to this organization',
          },
        ]);
      }
    }
    return {
      recordCategoryType: 'di',
      ...validated,
      original,
      changes,
    };
  } catch (error: any) {
    if (error instanceof z.ZodError) {
      console.log('===> ', error);
      const errors: IFieldErrors = error.format();
      throw new ValidationError(errors);
    }
    throw new Error(error?.message || '');
  }
};
